import { Component, effect, inject, OnDestroy, OnInit } from '@angular/core';
import { PrintFilterDataComponent } from "../../../../shared/components/print-filter-data/print-filter-data.component";
import { Subject, takeUntil } from "rxjs";
import { IReportFilter } from "../../../../utils/interfaces/report-filter";
import {
  IIncentiveReport,
  IIncentiveReportDraw,
  IIncentiveReportPagination
} from "../../../../utils/interfaces/incentive";
import { ActivatedRoute } from "@angular/router";
import { IUnit } from "../../../../utils/interfaces/unit";
import { LoadingService } from "../../../../shared/services/loading.service";
import { IncentiveService } from "../../../../shared/services/incentive.service";
import { DocumentTypeEnum, ExportToPdfService } from "../../../../shared/services/export-to-pdf.service";
import { PrintDocumentService } from "../../../../print-documents/print-document.service";
import { ReportFilterService } from "../../../../shared/services/report-filter.service";
import { DatePipe } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatInput } from "@angular/material/input";
import {
  ConfirmationModalComponent
} from "../../../../shared/components/confirmation-modal/confirmation-modal.component";
import { MatDialog } from "@angular/material/dialog";
import { DrawService } from "../../../../shared/services/draw.service";
import { AlertService } from "../../../../shared/services/alert.service";
import { IPaginationValue, PaginationComponent } from "../../../../shared/components/pagination/pagination.component";

@Component({
  selector: 'app-incentive',
  standalone: true,
  imports: [
    PrintFilterDataComponent,
    DatePipe,
    FormsModule,
    MatInput,
    PaginationComponent
  ],
  templateUrl: './incentive.component.html',
  styleUrl: './incentive.component.scss'
})
export class IncentiveComponent implements OnInit, OnDestroy {

  private readonly activatedRoute = inject(ActivatedRoute)
  private readonly loadingService = inject(LoadingService)
  private readonly incentiveService = inject(IncentiveService);
  private readonly exportToPdfService = inject(ExportToPdfService);
  private readonly printDocumentService = inject(PrintDocumentService);
  private readonly reportFilterService = inject(ReportFilterService);
  private readonly drawService = inject(DrawService);
  private readonly alertService = inject(AlertService);

  private readonly unsubscribe$ = new Subject();

  private readonly matDialog = inject(MatDialog);

  print = false;

  data: IIncentiveReportPagination[] = [];
  incentivePrinting: IIncentiveReportPagination[] = [];
  filter: IReportFilter | null = null;

  unit: IUnit[] = [];

  get today(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  constructor() {
    effect(
      () => {
        this.printDocument(this.exportToPdfService.getDocumentType());
      },
      {
        allowSignalWrites: true,
      }
    );
    effect(
      () => {
        this.filter = this.reportFilterService.getFilter();
        this.fetchData();
      },
      {
        allowSignalWrites: true,
      }
    );
  }

  ngOnInit(): void {
    this.unit = this.activatedRoute.snapshot.parent?.data['unitUserPoll']['data'];
  }

  isIncentiveExpired(endDate: string | Date): boolean {
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);
    return end < this.today;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }


  fetchData(): void {
    if (!this.filter) return;

    setTimeout(() => {
      this.loadingService.showLoading();
    }, 0);

    this.incentiveService
      .reportByBranchId({
        startDate: this.filter.startDate,
        endDate: this.filter.finishDate,
        branchesId: this.filter.branchesId
      })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        this.data = res.data.map((incentive: IIncentiveReport) => {
          return {
            ...incentive,
            pagination: {
              page: 1,
              take: 10,
            },
          } as IIncentiveReportPagination;
        })
          .sort((a, b) => {
            const aExpired = this.isIncentiveExpired(a.endDate);
            const bExpired = this.isIncentiveExpired(b.endDate);

            if (aExpired !== bExpired) {
              return aExpired ? 1 : -1;
            }
            return a.title.localeCompare(b.title);
          });

          this.incentivePrinting = [...this.data];

        setTimeout(() => {
          this.loadingService.closeLoading();
        }, 750);
      });
  }

  printDocument(document: DocumentTypeEnum | null): void {
    if (!document || !this.incentivePrinting || this.incentivePrinting.length === 0) {
      this.exportToPdfService.generatePDF(null);
      return;
    }

    this.printDocumentService.printDocument(DocumentTypeEnum.incentive, {
      dataIncentive: this.incentivePrinting,
      filter: this.filter,
    });
    this.exportToPdfService.generatePDF(null);
  }

  winnerDelivery(draw: IIncentiveReportDraw): void {
    if (!draw.winnerName) {
      this.alertService.warning('Informe o nome do ganhador antes de entregar.');
      return;
    }

    if (!draw.incentiveId) {
      this.alertService.warning('Incentivo não encontrado.');
      return;
    }

    const dialogRef = this.matDialog.open(ConfirmationModalComponent, {
      width: '500px',
      data: {
        title: 'Entregar',
        description: 'Tem certeza de que quer entregar?',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (result) {
          this.loadingService.showLoading();
          this.drawService.deliver({
            drawId: draw.id,
            winnerName: draw.winnerName
          })
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
              this.loadingService.closeLoading();
              this.fetchData();
            });
        }
      });
  }

  onPaginationChange(event: IPaginationValue, incentive: IIncentiveReportPagination): void {
    incentive.pagination = event;
  }

  drawDataPagination(incentiveId: string): IIncentiveReportDraw[] {
    if (!this.data || this.data.length === 0) {
      return [];
    }

    const incentiveIndex = this.data.findIndex((d) => d.id === incentiveId);

    if (incentiveIndex === -1) {
      return [];
    }

    const incentive = this.data[incentiveIndex];

    if (!incentive.draws || incentive.draws.length === 0) {
      return [];
    }

    const startIndex = (incentive.pagination.page - 1) * incentive.pagination.take;
    const endIndex = startIndex + incentive.pagination.take;

    return incentive.draws.slice(startIndex, endIndex);
  }

  exportToPdf(incentive: IIncentiveReportPagination): void {
  if (!this.filter) {
    this.alertService.warning(
      'Não foi possível gerar o relatório, filtro não selecionado.'
    );
    return;
  }

  this.incentivePrinting = [incentive];

  Promise.resolve().then(() => {
    this.exportToPdfService.generatePDF(DocumentTypeEnum.incentive);
  });
}

}
